import { useState, Fragment } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, ChevronLeft, ChevronRight, Copy, ExternalLink, Mail, Phone, SkipForward } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { BOMItem, SupplierOffer } from "@/types/chemistry";
import { formatDate } from "@/lib/formatDate";
import { workDaysBetween } from "@/lib/workDays";

function DeliveryDot({ date, targetDate }: { date: string; targetDate?: string }) {
  if (!targetDate) return <span className="text-xs">{formatDate(date)}</span>;
  const delivery = new Date(date);
  const target = new Date(targetDate);
  // Buffer = work days the delivery arrives *before* the target date
  const buffer = workDaysBetween(delivery, target);
  const color = buffer >= 3 ? "bg-success" : buffer >= 0 ? "bg-warning" : "bg-destructive";
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span className={`h-2 w-2 rounded-full ${color} shrink-0`} />
      {formatDate(date)}
    </span>
  );
}

interface SupplierDetailContentProps {
  item: BOMItem;
  onSelectSupplier: (itemId: string, offer: SupplierOffer) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export function SupplierDetailContent({
  item,
  onSelectSupplier,
  onNext,
  onPrevious,
  onSkip,
  hasNext,
  hasPrevious,
}: SupplierDetailContentProps) {
  const { toast } = useToast();
  const [prioritizeInventory, setPrioritizeInventory] = useState(false);
  const [autoPackageSize, setAutoPackageSize] = useState(true);
  const [offerOverrides, setOfferOverrides] = useState<Record<string, { inInventory?: boolean; inStock?: boolean }>>({});
  const [packageOverrides, setPackageOverrides] = useState<Record<string, string>>({});

  const toggleOfferField = (offerId: string, field: "inInventory" | "inStock") => {
    setOfferOverrides(prev => {
      const current = prev[offerId] || {};
      const offer = offers.find(o => o.id === offerId);
      const currentVal = current[field] ?? offer?.[field] ?? false;
      return { ...prev, [offerId]: { ...current, [field]: !currentVal } };
    });
  };

  const offers = item.supplierOffers ?? [];
  const resolvedOffers = offers.map(o => ({
    ...o,
    inInventory: offerOverrides[o.id]?.inInventory ?? o.inInventory,
    inStock: offerOverrides[o.id]?.inStock ?? o.inStock,
    packageSizeChosen: packageOverrides[o.id] ?? o.packageSizeChosen,
  }));
  const sorted = [...resolvedOffers].sort((a, b) => {
    if (prioritizeInventory) {
      if (a.inInventory !== b.inInventory) return a.inInventory ? -1 : 1;
      if (a.inStock !== b.inStock) return a.inStock ? -1 : 1;
    }
    return a.cost - b.cost;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Hero: structure + metadata */}
      <div className="px-5 pt-5 pb-4 border-b shrink-0">
        <div className="flex gap-5">
          <div className="flex items-center justify-center bg-muted/30 rounded-lg border border-border/50 p-3 w-[160px] h-[140px] shrink-0">
            {item.casNumber || item.chemicalName || item.materialName ? (
              <img
                src={`https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${encodeURIComponent(item.chemicalName || item.materialName)}/PNG?image_size=180x140`}
                alt={`Structure of ${item.chemicalName || item.materialName}`}
                className="max-h-[120px] max-w-full object-contain dark:invert"
                onError={(e) => {
                  const img = e.currentTarget;
                  const cas = item.casNumber;
                  if (cas && !img.dataset.retried) {
                    img.dataset.retried = "1";
                    img.src = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${encodeURIComponent(cas)}/PNG?image_size=180x140`;
                  } else if (item.materialName && !img.dataset.retried2) {
                    img.dataset.retried2 = "1";
                    img.src = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${encodeURIComponent(item.materialName)}/PNG?image_size=180x140`;
                  } else {
                    img.style.display = "none";
                    if (img.parentElement) {
                      img.parentElement.innerHTML =
                        '<span class="text-xs text-muted-foreground italic">Structure not available</span>';
                    }
                  }
                }}
              />
            ) : (
              <span className="text-xs text-muted-foreground italic">No compound data</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {item.chemicalName ? (
              <>
                <div className="flex items-center gap-2 mb-0.5">
                  <h2 className="text-lg font-bold tracking-tight">{item.chemicalName}</h2>
                  <Badge
                    variant="outline"
                    className={`text-[10px] border-0 ${
                      item.status === "selected"
                        ? "bg-primary/10 text-primary"
                        : item.status === "ordered"
                        ? "bg-success/10 text-success"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {item.status === "not_ordered" ? "Not Selected" : item.status === "selected" ? "Supplier Selected" : "Ordered"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground font-mono mb-2">{item.materialName}</p>
              </>
            ) : (
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-bold tracking-tight">{item.materialName}</h2>
                <Badge
                  variant="outline"
                  className={`text-[10px] border-0 ${
                    item.status === "selected"
                      ? "bg-primary/10 text-primary"
                      : item.status === "ordered"
                      ? "bg-success/10 text-success"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {item.status === "not_ordered" ? "Not Selected" : item.status === "selected" ? "Supplier Selected" : "Ordered"}
                </Badge>
              </div>
            )}
            <div className="grid grid-cols-[1fr_1fr] gap-x-8 gap-y-0 text-[13px]">
              {[
                [
                  { label: "Amount needed", value: `${item.quantity.toFixed(2)} ${item.unit}` },
                  { label: "CAS No", value: item.casNumber || "—", mono: true },
                ],
                [
                  { label: "Latest Delivery Date", value: formatDate(item.targetDate) },
                  { label: "CO₂eq", value: item.co2eq?.toFixed(2) ?? "—" },
                ],
                [
                  { label: "Required for", value: `Step ${item.stepIds.length > 1 ? item.stepIds.map((_, i) => i + 1).join(", ") : "1"}` },
                  { label: "Molar Mass", value: item.molarWeight != null ? `${item.molarWeight.toFixed(2)} g/mol` : "—", copyable: item.molarWeight != null ? item.molarWeight.toFixed(2) : undefined },
                ],
              ].map((row, ri) => (
                <Fragment key={ri}>
                  {row.map((cell) => (
                    <div key={cell.label} className="flex justify-between items-baseline py-1.5 border-b border-border/40 last:border-b-0">
                      <span className="text-muted-foreground">{cell.label}</span>
                      {'copyable' in cell && cell.copyable ? (
                        <span
                          className="font-medium text-right inline-flex items-center gap-1 group/copy cursor-pointer"
                          onClick={() => { navigator.clipboard.writeText(cell.copyable!); toast({ title: "Copied", description: `${cell.copyable} g/mol` }); }}
                        >
                          {cell.value}
                          <Copy className="h-3 w-3 text-muted-foreground" />
                        </span>
                      ) : (
                        <span className={`font-medium text-right ${cell.mono ? "font-mono" : ""} ${'capitalize' in cell && cell.capitalize ? "capitalize" : ""}`}>
                          {cell.value}
                        </span>
                      )}
                    </div>
                  ))}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Toggles + Nav */}
      <div className="px-5 py-2.5 border-b flex items-center justify-between shrink-0">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2">
            <Switch id="prio-inv-inline" checked={prioritizeInventory} onCheckedChange={setPrioritizeInventory} />
            <Label htmlFor="prio-inv-inline" className="text-xs">Prioritize Inventory</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="auto-pkg-inline" checked={autoPackageSize} onCheckedChange={setAutoPackageSize} />
            <Label htmlFor="auto-pkg-inline" className="text-xs">Auto-Select Package Size</Label>
          </div>
        </div>
        {(onPrevious || onNext || onSkip) && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" disabled={!hasPrevious} onClick={onPrevious}>
              <ChevronLeft className="h-3 w-3" /> Prev
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" disabled={!hasNext} onClick={onNext}>
              Next <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Supplier table */}
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs whitespace-nowrap">Supplier</TableHead>
              <TableHead className="text-xs whitespace-nowrap">Item #</TableHead>
              <TableHead className="text-xs whitespace-nowrap">Purity</TableHead>
              <TableHead className="text-xs whitespace-nowrap">Inventory</TableHead>
              <TableHead className="text-xs whitespace-nowrap">In Stock</TableHead>
              <TableHead className="text-xs whitespace-nowrap">Earliest Delivery</TableHead>
              <TableHead className="text-xs whitespace-nowrap">Package Sizes</TableHead>
              <TableHead className="text-xs whitespace-nowrap text-center">Units</TableHead>
              <TableHead className="text-xs whitespace-nowrap text-right">Cost</TableHead>
              <TableHead className="text-xs whitespace-nowrap text-right">CO₂e/g</TableHead>
              <TableHead className="text-xs whitespace-nowrap"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-sm text-muted-foreground py-8">
                  No supplier offers available.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((offer) => {
                const isSelected = item.supplierName === offer.supplierName && item.status === "selected";
                return (
                  <TableRow key={offer.id} className={isSelected ? "bg-primary/5" : ""}>
                    <TableCell className="text-sm font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {offer.supplierName}
                        <a href={offer.url || "#"} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" title="Open supplier">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </span>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{offer.itemNumber}</TableCell>
                    <TableCell className="text-xs">{offer.purity}</TableCell>
                    <TableCell>
                      <button onClick={() => toggleOfferField(offer.id, "inInventory")} className="cursor-pointer">
                        {offer.inInventory
                          ? <Badge variant="secondary" className="text-[10px] bg-success/15 text-success border-success/30 hover:bg-success/25 transition-colors">Yes</Badge>
                          : <span className="text-xs text-muted-foreground hover:text-foreground transition-colors">No</span>}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button onClick={() => toggleOfferField(offer.id, "inStock")} className="cursor-pointer">
                        {offer.inStock
                          ? <Badge variant="secondary" className="text-[10px] bg-success/15 text-success border-success/30 hover:bg-success/25 transition-colors">Yes</Badge>
                          : <span className="text-xs text-muted-foreground hover:text-foreground transition-colors">No</span>}
                      </button>
                    </TableCell>
                    <TableCell>
                      <DeliveryDot date={offer.earliestDelivery} targetDate={item.targetDate} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {offer.packageSizes.map((s) => (
                          <Badge
                            key={s}
                            variant={s === offer.packageSizeChosen ? "default" : "outline"}
                            className="text-[10px] px-1.5 py-0 cursor-pointer"
                            onClick={() => setPackageOverrides(prev => ({ ...prev, [offer.id]: s }))}
                          >
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-center whitespace-nowrap">
                      {(() => {
                        const chosenPkg = offer.packageSizeChosen;
                        if (!chosenPkg) return "—";
                        const pkgGrams = parseFloat(chosenPkg);
                        if (!pkgGrams || !item.quantity) return "—";
                        let qtyInGrams = item.quantity;
                        if (item.unit === "kg") qtyInGrams = item.quantity * 1000;
                        else if (item.unit === "mg") qtyInGrams = item.quantity / 1000;
                        const pkgUnit = chosenPkg.replace(/[\d.]/g, "").trim().toLowerCase();
                        let pkgInGrams = pkgGrams;
                        if (pkgUnit === "kg") pkgInGrams = pkgGrams * 1000;
                        else if (pkgUnit === "mg") pkgInGrams = pkgGrams / 1000;
                        return Math.ceil(qtyInGrams / pkgInGrams);
                      })()}
                    </TableCell>
                    <TableCell className="text-xs text-right whitespace-nowrap">
                      <span className="font-medium">€{offer.cost.toFixed(2)}</span>
                      <br />
                      <span className="text-muted-foreground">€{offer.costPerUnit.toFixed(2)}/g</span>
                    </TableCell>
                    <TableCell className="text-xs text-right">{offer.co2ePerGram.toFixed(3)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant={isSelected ? "secondary" : "default"}
                        className="h-7 text-xs gap-1"
                        onClick={() => onSelectSupplier(item.id, offer)}
                      >
                        {isSelected ? <><Check className="h-3 w-3" /> Selected</> : "Select"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
